import { createRoot } from 'react-dom/client';
import { useState, useMemo, useEffect, useRef } from 'react';
import Series from './series';
import { filterSeries } from './data';
import Filters from './filters';
import ReactShadowRoot from 'react-shadow-root';
import { PlayerProvider } from './player';
import css from './style.scss'
import { Search } from 'lucide-react';

import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

const CustomEmotionCacheProvider = ({ children }) => {
	const [cache, setCache] = useState(null);
	const containerRef = useRef(null);

	useEffect(() => {
		const shadowRootCache = createCache({
			key: 'redeemer',
			container: containerRef.current,
			prepend: true,
		})

		setCache(shadowRootCache);
	}, []);

	return (
		<div ref={containerRef}>
			{ cache && <CacheProvider value={cache}>{children}</CacheProvider> }
		</div>
	);
};

/**
 * Main App component
 *
 * @returns {JSX.Element}
 */
function App() {
	const [search, setSearch] = useState('')
	const [searchKeyword, setSearchKeyword] = useState('')
	const [page, setPage] = useState(1)
	const [filters, setFilters] = useState({})
	const scrollRef = useRef(null)

	const results = useMemo(() => {
		return filterSeries(filters)
	}, [filters])

	useEffect(() => {
		const newFilters = {...filters}
		if(searchKeyword) {
			newFilters.search = searchKeyword
		} else {
			delete newFilters.search
		}
		setFilters(newFilters)
	}, [searchKeyword])

	useEffect(() => {
		setPage(1) // Reset page when search changes
	}, [results])

	useEffect(() => {
		const observer = new IntersectionObserver(entries => {
			const entry = entries[0]
			if(entry.isIntersecting) {
				setPage(page => page + 1)
			}
		})

		observer.observe(scrollRef.current)

		return () => {
			observer.disconnect()
		}
	}, [])

	// Debounce search
	useEffect(() => {
		let timeout = setTimeout(() => {
			setSearchKeyword(search)
		}, 500)
		return () => clearTimeout(timeout)
	}, [search])

	return (
		<div className="redeemer-app">
			<PlayerProvider>
				<Filters
					filters={filters}
					setFilters={setFilters}
				/>
				<div className="redeemer-app--search">
					<input
						type="text"
						placeholder="Search by title"
						className="search"
						value={search}
						onChange={e => {
							setSearch(e.target.value)
						}}
					/>
					<Search size={16} className="search-icon" />
				</div>
				<div className="series-list">
					{
						results.slice(0, page * 10).map(series => {
							return (
								<Series
									search={searchKeyword}
									key={series.id}
									series={series}
								/>
							)
						})
					}
				</div>
				<div ref={scrollRef}></div>
			</PlayerProvider>
		</div>
	);
}

const element = document.getElementById('redeemer-root')

if(element) {
	// Find the existing viewport meta tag
	let meta = document.querySelector('meta[name="viewport"]');
	if (!meta) {
		// If not found, create a new one
		meta = document.createElement('meta');
		meta.name = 'viewport';
		document.getElementsByTagName('head')[0].appendChild(meta);
	}
	// Update the content of the viewport meta tag
	meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';

	const root = createRoot(element);

	root.render(
		<ReactShadowRoot>
			<style>{css}</style>
			<CustomEmotionCacheProvider>
				<App />
			</CustomEmotionCacheProvider>
		</ReactShadowRoot>
	)
}